/**
 * The Valuation discipline's project list, and the four ways to start one.
 *
 * A valuation is its own project: it can be pinned to a set of accounts, or stand on
 * figures somebody supplied. The four sources reduce to the same six numbers a year,
 * so this screen differs from the accounting list mainly in what it offers to create
 * and what it shows about where each valuation's figures came from.
 *
 * Origin is on every row on purpose. A number is worth little without knowing what
 * it was computed from - and for a linked valuation, WHEN, because the figures are
 * pinned rather than live.
 */

import { useEffect, useState } from 'react';

import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import { useMAccountingStore } from '../../../store/m-accounting';
import {
  useValuationStore,
  type SourceKind,
  type Units,
} from '../../../store/valuation-projects';
import { Button } from '../ui/Button';
import { Notice, Panel, ProjectRow, ProjectsPage } from '../ui/ProjectsPage';
import { FolderedProjects } from '../ui/ProjectFolders';
import type { Discipline } from '../disciplines';
import ValuationFiguresForm from './ValuationFiguresForm';

const SOURCE_LABELS: Record<SourceKind, string> = {
  linked: 'From a set of accounts',
  form: 'Figures entered by hand',
  excel: 'Imported from a spreadsheet',
  pnl: 'From a profit and loss account',
};

function money(value: number | null): string {
  if (value === null || Number.isNaN(value)) return '—';
  return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

export default function ValuationProjectList({
  discipline,
  onLeaveDiscipline,
  onOpen,
}: {
  discipline: Discipline;
  onLeaveDiscipline: () => void;
  onOpen: (id: string) => void;
}) {
  const list = useValuationStore((s) => s.list);
  const isLoading = useValuationStore((s) => s.isLoading);
  const error = useValuationStore((s) => s.error);
  const dismissError = useValuationStore((s) => s.dismissError);
  const listValuations = useValuationStore((s) => s.listValuations);
  const create = useValuationStore((s) => s.create);
  const remove = useValuationStore((s) => s.remove);
  const samples = useValuationStore((s) => s.samples);
  const listSamples = useValuationStore((s) => s.listSamples);
  const openSample = useValuationStore((s) => s.openSample);

  // The accounting projects available to pin to - IAS accounting's models and
  // Baseline Accounting's projects. The valuation stands on whichever is chosen.
  const models = useFinancialModelV2Store((s) => s.models);
  const listModels = useFinancialModelV2Store((s) => s.listModels);
  const baselineProjects = useMAccountingStore((s) => s.list);
  const listBaseline = useMAccountingStore((s) => s.loadList);
  const [linkKind, setLinkKind] = useState<'ias' | 'baseline'>('ias');

  const [starting, setStarting] = useState<SourceKind | null>(null);
  const [name, setName] = useState('');
  const [modelId, setModelId] = useState('');
  const [units, setUnits] = useState<Units>('units');

  useEffect(() => {
    void listValuations();
    void listSamples();
    // Loaded here rather than when "From a set of accounts" is chosen, so the
    // option can say how many there are - or that there are none, which is the
    // case that needs explaining.
    void listModels();
    void listBaseline();
  }, [listValuations, listModels, listBaseline]);

  const startLinked = async () => {
    if (!modelId) return;
    const id = await create({
      name: name || 'Valuation',
      source: { kind: 'linked', modelId, units, modelKind: linkKind },
    });
    if (id) {
      setStarting(null);
      setName('');
      onOpen(id);
    }
  };

  const field =
    'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-100';

  return (
    <ProjectsPage
      discipline={discipline}
      onLeaveDiscipline={onLeaveDiscipline}
      intro="What a business is worth, from finished statements: pin a valuation to a set of accounts, or value it from figures you supply."
    >
      {error && <Notice onDismiss={dismissError}>{error}</Notice>}

      {/* ── Starting one ─────────────────────────────────────── */}
      <Panel
        className="mt-8"
        title="Start a valuation"
        description="Choose where its figures come from. Every source reduces to the same six numbers a year."
      >
        <div className="flex flex-wrap gap-2">
          {(Object.keys(SOURCE_LABELS) as SourceKind[]).map((kind) => (
            <Button
              key={kind}
              variant={starting === kind ? 'primary' : 'secondary'}
              aria-pressed={starting === kind}
              onClick={() => setStarting(starting === kind ? null : kind)}
            >
              {SOURCE_LABELS[kind]}
            </Button>
          ))}
        </div>

        {starting && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <label className="block text-xs font-medium text-gray-700">
              Name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Acme Ltd — base case"
                className={field}
              />
            </label>

            <label className="mt-3 block text-xs font-medium text-gray-700">
              Figures are in
              <select
                value={units}
                onChange={(event) => setUnits(event.target.value as Units)}
                className={field.replace('w-full', 'w-auto')}
              >
                <option value="units">Actual amounts</option>
                <option value="thousands">Thousands</option>
                <option value="millions">Millions</option>
              </select>
              {/* Asked once, here. A valuation that mixes units is wrong by a
                  factor of a thousand, which reads as a modelling disagreement
                  rather than the units error it is. */}
              <span className="mt-1 block font-normal text-gray-500">
                Every figure in this valuation is read in these units.
              </span>
            </label>

            {starting === 'linked' && (
              <div className="mt-3">
                <div className="text-xs font-medium text-gray-700">Value the accounts in</div>
                <div className="mt-1 flex gap-2" role="radiogroup" aria-label="Which accounts">
                  {(
                    [
                      ['ias', 'IAS accounting', 'accounting'],
                      ['baseline', 'Baseline Accounting', 'm-accounting'],
                    ] as const
                  ).map(([kind, label, disciplineId]) => (
                    // Each option in its own discipline's colour, as everywhere else.
                    <span key={kind} data-discipline={disciplineId}>
                      <Button
                        size="sm"
                        role="radio"
                        aria-checked={linkKind === kind}
                        variant={linkKind === kind ? 'primary' : 'secondary'}
                        onClick={() => {
                          setLinkKind(kind);
                          setModelId('');
                        }}
                      >
                        {label}
                      </Button>
                    </span>
                  ))}
                </div>
                <label className="mt-3 block text-xs font-medium text-gray-700">
                  Accounts to value
                  <select
                    value={modelId}
                    onChange={(event) => setModelId(event.target.value)}
                    className={field}
                  >
                    <option value="">
                      {linkKind === 'ias'
                        ? 'Choose an IAS accounting project…'
                        : 'Choose a Baseline Accounting project…'}
                    </option>
                    {(linkKind === 'ias' ? models : baselineProjects).map((model) => (
                      <option key={model.id} value={model.id}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                </label>
                {(linkKind === 'ias' ? models : baselineProjects).length === 0 && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    There are no {linkKind === 'ias' ? 'IAS accounting' : 'Baseline Accounting'}{' '}
                    projects yet. Switch to it to build one, choose the other, or start
                    this valuation from figures instead.
                  </p>
                )}
                <p className="mt-2 text-xs text-gray-600">
                  The figures are <strong>copied</strong> from those accounts now,
                  not read from them as you go. Editing the accounts later will not
                  change this valuation until you refresh it — so a number you have
                  sent someone stays the number you sent.
                </p>
                <Button
                  variant="primary"
                  className="mt-3"
                  disabled={!modelId}
                  onClick={() => void startLinked()}
                >
                  Create it
                </Button>
              </div>
            )}

            {starting !== 'linked' && (
              <ValuationFiguresForm
                kind={starting}
                name={name || 'Valuation'}
                units={units}
                onCreated={(id) => {
                  setStarting(null);
                  setName('');
                  onOpen(id);
                }}
              />
            )}
          </div>
        )}
      </Panel>

      {/* ── The list ─────────────────────────────────────────── */}
      <FolderedProjects
        discipline={discipline.id}
        onCopied={() => void listValuations()}
        isLoading={isLoading}
        empty="No valuations yet. Start one above — from a set of accounts you have already built, or from figures if you have no accounts for it."
        samples={samples.map((sample) => ({
          ...sample,
          onOpen: () =>
            void openSample(sample.key).then((id) => {
              if (id) onOpen(id);
            }),
        }))}
        projects={list.map((row) => ({
          id: row.id,
          render: (folderControl, options) => (
          <ProjectRow
            key={row.id}
            folderControl={folderControl}
            title={row.name}
            meta={
              <>
                {SOURCE_LABELS[row.sourceKind]}
                {row.modelName &&
                  ` · ${row.modelName}${row.modelKind === 'baseline' ? ' (Baseline Accounting)' : ' (IAS accounting)'}`}
                {/* WHEN it was pinned, because the figures are a copy. */}
                {row.pinnedAt && ` · pinned ${row.pinnedAt.slice(0, 10)}`}
                {row.years.length > 0 &&
                  ` · FY${row.years[0]}–${row.years[row.years.length - 1]}`}
              </>
            }
            // A valuation whose figures cannot support one says so on the row, so
            // nobody has to open it to find out.
            note={row.notes.length > 0 ? row.notes[0] : undefined}
            aside={
              <div className="shrink-0 text-right">
                <div className="font-mono text-sm font-medium text-gray-900">
                  {money(row.equityValue)}
                </div>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">equity value</div>
              </div>
            }
            onOpen={() => onOpen(row.id)}
            onDelete={options?.sample ? undefined : () => void remove(row.id)}
          />
          ),
        }))}
      />
    </ProjectsPage>
  );
}
